import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
	Overlay,
	Modal,
	CloseX,
	Title,
	Subtitle,
	Section,
	FieldLabel,
	EmailRow,
	EmailText,
	InlineButton,
	StyledInput,
	MessageBox,
	ActionRow,
	PrimaryButton,
	SecondaryButton,
	MailAppLink,
	Hint,
	CopiedTag,
} from './SupportContactModal.styles';

export const SUPPORT_EMAIL = 'ownersupport@kozyguru.com';

const sydneyNow = () =>
	new Date().toLocaleString('en-AU', {
		timeZone: 'Australia/Sydney',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});

// navigator.clipboard 在非 https / 舊瀏覽器不存在，保留 execCommand fallback
const copyToClipboard = async (text) => {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch (err) {
		console.warn('Clipboard API failed, falling back:', err);
	}

	try {
		const helper = document.createElement('textarea');
		helper.value = text;
		helper.setAttribute('readonly', '');
		helper.style.position = 'fixed';
		helper.style.opacity = '0';
		document.body.appendChild(helper);
		helper.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(helper);
		return ok;
	} catch (err) {
		console.warn('Clipboard fallback failed:', err);
		return false;
	}
};

/**
 * 忘記密碼 / 聯絡客服彈窗。
 *
 * 不直接跳 mailto：Windows 上多數使用者沒有設定預設郵件程式，
 * 會跳「選擇開啟方式」視窗或整頁卡住。這裡改成把求助內容準備好，
 * 讓使用者用「複製」或網頁版 Gmail / Outlook 寄出，mailto 只留作選項之一。
 */
const SupportContactModal = ({
	isOpen,
	onClose,
	accountEmail = '',
	isSuperAdmin = false,
}) => {
	const [email, setEmail] = useState(accountEmail);
	const [copiedField, setCopiedField] = useState('');
	const copyTimerRef = useRef(null);

	useEffect(() => {
		if (isOpen) setEmail(accountEmail);
	}, [isOpen, accountEmail]);

	// Esc 關閉
	useEffect(() => {
		if (!isOpen) return undefined;

		const onKeyDown = (e) => {
			if (e.key === 'Escape') onClose?.();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, onClose]);

	useEffect(() => () => clearTimeout(copyTimerRef.current), []);

	// 開啟當下就固定住送出時間，避免每次 render 都跳動
	const requestTime = useMemo(() => (isOpen ? sydneyNow() : ''), [isOpen]);

	const subject = isSuperAdmin
		? 'Super Admin Password Reset Request'
		: 'Password Reset Request';

	const body = useMemo(
		() =>
			[
				'Hi KozyGuru Support,',
				'',
				`I have forgotten my ${isSuperAdmin ? 'Super Admin ' : ''}password and need assistance resetting it.`,
				'',
				`Account Email / Username: ${email || '(please fill in your account email)'}`,
				`Request Time: ${requestTime} (Sydney Time)`,
				'',
				'Please help me reset my password.',
				'',
				'Thank you.',
			].join('\n'),
		[email, requestTime, isSuperAdmin]
	);

	const flagCopied = (field) => {
		setCopiedField(field);
		clearTimeout(copyTimerRef.current);
		copyTimerRef.current = setTimeout(() => setCopiedField(''), 2000);
	};

	const handleCopyEmail = async () => {
		if (await copyToClipboard(SUPPORT_EMAIL)) flagCopied('email');
	};

	const handleCopyMessage = async () => {
		if (await copyToClipboard(`To: ${SUPPORT_EMAIL}\nSubject: ${subject}\n\n${body}`)) {
			flagCopied('message');
		}
	};

	const openCompose = (url) => {
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const gmailUrl =
		'https://mail.google.com/mail/?view=cm&fs=1' +
		`&to=${encodeURIComponent(SUPPORT_EMAIL)}` +
		`&su=${encodeURIComponent(subject)}` +
		`&body=${encodeURIComponent(body)}`;

	const outlookUrl =
		'https://outlook.office.com/mail/deeplink/compose?' +
		`to=${encodeURIComponent(SUPPORT_EMAIL)}` +
		`&subject=${encodeURIComponent(subject)}` +
		`&body=${encodeURIComponent(body)}`;

	const mailtoUrl =
		`mailto:${SUPPORT_EMAIL}` +
		`?subject=${encodeURIComponent(subject)}` +
		`&body=${encodeURIComponent(body)}`;

	if (!isOpen) return null;

	return (
		<Overlay onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
			<Modal role="dialog" aria-modal="true" aria-label="Contact support to reset your password">
				<CloseX type="button" onClick={onClose} aria-label="Close">
					×
				</CloseX>

				<Title>Forgot your password?</Title>
				<Subtitle>
					Password resets are handled by our support team. Send us the request below and
					we'll get you back in.
				</Subtitle>

				<Section>
					<FieldLabel>Support email</FieldLabel>
					<EmailRow>
						<EmailText>{SUPPORT_EMAIL}</EmailText>
						<InlineButton type="button" onClick={handleCopyEmail}>
							{copiedField === 'email' ? 'Copied' : 'Copy'}
						</InlineButton>
					</EmailRow>
				</Section>

				<Section>
					<FieldLabel htmlFor="support-account-email">Your account email</FieldLabel>
					<StyledInput
						id="support-account-email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="you@example.com"
						autoCapitalize="off"
					/>
				</Section>

				<Section>
					<FieldLabel>
						Your request
						{copiedField === 'message' && <CopiedTag>Copied to clipboard</CopiedTag>}
					</FieldLabel>
					<MessageBox readOnly value={body} onFocus={(e) => e.target.select()} />
					<ActionRow>
						<PrimaryButton type="button" onClick={handleCopyMessage}>
							Copy request
						</PrimaryButton>
					</ActionRow>
				</Section>

				<Section style={{ marginBottom: 0 }}>
					<FieldLabel>Or send it straight away</FieldLabel>
					<ActionRow>
						<SecondaryButton type="button" onClick={() => openCompose(gmailUrl)}>
							Gmail
						</SecondaryButton>
						<SecondaryButton type="button" onClick={() => openCompose(outlookUrl)}>
							Outlook
						</SecondaryButton>
						<MailAppLink href={mailtoUrl}>Mail app</MailAppLink>
					</ActionRow>
					<Hint>
						"Mail app" only works if your computer has a desktop email program set up. If
						nothing happens, use "Copy request" and paste it into your usual email.
					</Hint>
				</Section>
			</Modal>
		</Overlay>
	);
};

export default SupportContactModal;
